import { LocalDate } from "@js-joda/core";
import React from "react";
import { PendingPatientTasksBottomSheet } from "./components/PendingPatientTasksBottomSheet/PendingPatientTasksBottomSheet";
import { isLoading, Loading } from "./Loading";
import { AppTasksState } from "./system/AppState/AppTasksState";

interface Params {
  tasksState: AppTasksState | Loading;
  onClickReviewTasks: (nextPendingTaskDate: LocalDate) => void;
}

export function usePendingPatientTasksBottomSheet(params: Params) {
  const [isPendingPatientTasksBottomSheetOpen, setPendingPatientTasksBottomSheetOpen] =
    React.useState(false);

  const handleRequestClose = React.useCallback(() => {
    setPendingPatientTasksBottomSheetOpen(false);
  }, []);

  const nextPendingTaskDate = React.useMemo(() => {
    const now = LocalDate.now();
    const tasksState = params.tasksState;
    if (isLoading(tasksState)) {
      return now;
    }

    const pendingTasks = tasksState.tasks.filter(
      (patientTask) =>
        patientTask.type === "PENDING" && patientTask.startDate.isAfter(LocalDate.now())
    );

    if (pendingTasks.length() === 0) {
      return now;
    }

    const sortedTasks = pendingTasks
      .toArray()
      .sort((a, b) => (a.startDate.isAfter(b.startDate) ? 1 : -1));
    return sortedTasks[0].startDate;
  }, [params.tasksState]);

  const handleClickReviewTasks = React.useCallback(() => {
    setPendingPatientTasksBottomSheetOpen(false);
    params.onClickReviewTasks.call(null, nextPendingTaskDate);
  }, [nextPendingTaskDate, params.onClickReviewTasks]);

  const totalPendingPatientTasks = React.useMemo(() => {
    const tasksState = params.tasksState;
    const pendingTasksLength = isLoading(tasksState)
      ? 0
      : tasksState.tasks
          .filter(
            (patientTask) =>
              patientTask.type === "PENDING" && patientTask.startDate.isAfter(LocalDate.now())
          )
          .length();
    if (pendingTasksLength > 0) {
      setPendingPatientTasksBottomSheetOpen(true);
    }
    return pendingTasksLength;
  }, [params.tasksState]);

  const bottomSheetProps: React.ComponentProps<typeof PendingPatientTasksBottomSheet> = {
    isOpen: isPendingPatientTasksBottomSheetOpen,
    pendingTasksAmount: totalPendingPatientTasks,
    onClickReviewTasks: handleClickReviewTasks,
    onRequestClose: handleRequestClose,
  };

  return {
    bottomSheetProps,
    handleRequestClosePendingPatientTasksBottomSheet: handleRequestClose,
    handleClickReviewTasks: handleClickReviewTasks,
    totalPendingPatientTasks: totalPendingPatientTasks,
    isPendingPatientTasksBottomSheetOpen: isPendingPatientTasksBottomSheetOpen,
  };
}
